import React, { Suspense, useEffect, useState, useRef, forwardRef } from "react";
import { Canvas, useThree, useLoader } from "react-three-fiber";
import { OrbitControls, useGLTF, useAnimations } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import "./ModelViewer.css";
import PropTypes from "prop-types";
import { useDevMode } from '../../contexts/DevModeContext';

// Preload GLTF models to avoid CORS or loading issues
const preloadGLTF = (path) => {
  return new Promise((resolve, reject) => {
    // Create a new GLTF loader
    const loader = new GLTFLoader();
    
    // Log the attempt to load
    console.log(`Preloading GLTF model from: ${path}`);
    
    // Attempt to load the model
    loader.load(
      path,
      // onLoad callback
      (gltf) => {
        console.log(`Successfully preloaded model: ${path}`);
        resolve(gltf);
      },
      // onProgress callback
      (xhr) => {
        const loadPercentage = (xhr.loaded / xhr.total) * 100;
        console.log(`Loading model: ${loadPercentage.toFixed(2)}% loaded`);
      },
      // onError callback
      (error) => {
        console.error(`Error preloading model: ${path}`, error);
        reject(error);
      }
    );
  });
};

// Create a forwarded ref for the Model component
const Model = React.forwardRef(({ 
  modelPath, 
  onAnimationsLoaded, 
  onAnimationChange, 
  wireframe = false,
  animationSpeed = 1,
  onError
}, ref) => {
  const groupRef = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [currentAnimation, setCurrentAnimation] = useState(null);

  // Always call useGLTF at the top level
  const gltf = useGLTF(modelPath, true, (error) => {
    setLoadError(error);
    if (onError) onError(error);
  });

  // Always call useAnimations, even if gltf is undefined
  const { scene, animations } = gltf || { scene: null, animations: [] };
  const { actions, names, mixer } = useAnimations(animations, groupRef);

  useEffect(() => {
    if (scene) {
      setIsLoading(false);
    }
  }, [scene]);
  
  // Apply wireframe to all materials
  useEffect(() => {
    if (scene) {
      scene.traverse((child) => {
        if (child.isMesh && child.material) {
          // If material is an array, handle all materials
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => {
              mat.wireframe = wireframe;
            });
          } else {
            child.material.wireframe = wireframe;
          }
        }
      });
    }
  }, [scene, wireframe]);
  
  // Update animation speed
  useEffect(() => {
    if (mixer) {
      mixer.timeScale = animationSpeed;
    }
  }, [mixer, animationSpeed]);
  
  // Expose animations to parent
  useEffect(() => {
    if (onAnimationsLoaded && names) {
      onAnimationsLoaded({
        hasAnimations: names.length > 0,
        animationNames: names
      });
    }
  }, [names, onAnimationsLoaded]);
  
  // Effect to play the first animation if available
  useEffect(() => {
    if (names && names.length > 0 && !currentAnimation) {
      const animationName = names[0];
      setCurrentAnimation(animationName);
      
      // Play the animation
      const action = actions[animationName];
      if (action) {
        action.reset().fadeIn(0.5).play();
      }
      
      // Notify parent about animation change
      if (onAnimationChange) {
        onAnimationChange(animationName);
      }
    }
    
    return () => {
      // Cleanup animations
      if (currentAnimation && actions && actions[currentAnimation]) {
        actions[currentAnimation].fadeOut(0.5);
      }
    };
  }, [actions, names, currentAnimation, onAnimationChange]);

  // Function to toggle between animations
  const toggleAnimation = () => {
    if (names && names.length > 1) {
      // Get current animation index
      const currentIndex = names.indexOf(currentAnimation);
      // Calculate next animation index (cycle through animations)
      const nextIndex = (currentIndex + 1) % names.length;
      const nextAnimation = names[nextIndex];
      
      // Fade out current animation
      if (currentAnimation && actions && actions[currentAnimation]) {
        actions[currentAnimation].fadeOut(0.5);
      }
      
      // Fade in next animation
      if (actions && actions[nextAnimation]) {
        actions[nextAnimation].reset().fadeIn(0.5).play();
        setCurrentAnimation(nextAnimation);
        
        // Notify parent about animation change
        if (onAnimationChange) {
          onAnimationChange(nextAnimation);
        }
      }
    }
  };
  
  // Function to pause/play animation
  const togglePlayPause = () => {
    if (currentAnimation && actions && actions[currentAnimation]) {
      const action = actions[currentAnimation];
      // Toggle paused state by checking timeScale
      if (action.paused) {
        action.paused = false;
      } else {
        action.paused = true;
      }
      return !action.paused; // Return new state
    }
    return false;
  };
  
  // Expose functions via ref
  useEffect(() => {
    if (ref) {
      // Use Object.assign to update the ref's current value
      // This avoids recreating the object on every render
      const methods = {
        toggleAnimation,
        togglePlayPause,
        actions,
        currentAnimation,
        scene
      };
      
      if (typeof ref === 'function') {
        ref(methods);
      } else {
        Object.assign(ref, methods);
      }
    }
  }, [toggleAnimation, togglePlayPause, actions, currentAnimation, scene, ref]);
  
  // If there's an error, render a fallback
  if (loadError) {
    return (
      <mesh>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="red" />
      </mesh>
    );
  }
  
  // If no scene is available yet, render nothing
  if (!scene) {
    return null;
  }
  
  return (
    <group ref={groupRef}>
      <primitive object={scene} />
    </group>
  );
});

// Camera Control Component
const CameraControls = ({ resetRef }) => {
  const { camera, gl } = useThree();
  const controlsRef = useRef();
  
  // Reset camera view
  const resetCamera = () => {
    if (controlsRef.current) {
      controlsRef.current.reset();
    }
  };
  
  // Expose reset function via ref
  useEffect(() => {
    if (resetRef) {
      const methods = { resetCamera };
      
      if (typeof resetRef === 'function') {
        resetRef(methods);
      } else {
        Object.assign(resetRef, methods);
      }
    }
  }, [resetRef]);

  // Update camera position display
  useEffect(() => {
    const updateCameraPosition = () => {
      const position = camera.position;
      const roundedPosition = {
        x: Math.round(position.x * 100) / 100,
        y: Math.round(position.y * 100) / 100,
        z: Math.round(position.z * 100) / 100
      };
      const cameraPositionElement = document.getElementById('camera-position');
      if (cameraPositionElement) {
        cameraPositionElement.textContent = 
          `Camera: [${roundedPosition.x}, ${roundedPosition.y}, ${roundedPosition.z}]`;
      }
    };

    const interval = setInterval(updateCameraPosition, 100);
    return () => clearInterval(interval);
  }, [camera]);
  
  return (
    <OrbitControls
      ref={controlsRef}
      args={[camera, gl.domElement]}
      enableZoom={true}
      maxDistance={35}
      minDistance={1}
    />
  );
};

const ModelViewer = forwardRef(({ modelPath, cameraPosition, cameraFov, className = '' }, ref) => {
  const { isDevMode } = useDevMode();
  const [modelInfo, setModelInfo] = useState({
    hasAnimations: false,
    animationNames: []
  });
  
  const [currentAnimation, setCurrentAnimation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [wireframe, setWireframe] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(1);
  const [loadingState, setLoadingState] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [sceneKey, setSceneKey] = useState(Date.now());
  
  const modelRef = useRef(null);
  const resetRef = useRef(null);
  const [hasAnimations, setHasAnimations] = useState(false);
  const [cameraPositionDisplay, setCameraPositionDisplay] = useState(cameraPosition);
  
  // Handle visibility and focus changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Force scene reload by updating the key
        setSceneKey(Date.now());
      }
    };

    const handleFocus = () => {
      // Force scene reload by updating the key
      setSceneKey(Date.now());
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Check if model file exists and preload it
  useEffect(() => {
    if (!modelPath) {
      setLoadingState("error");
      setErrorMessage("No model path provided");
      return;
    }
    
    setLoadingState("loading");
    
    // Try to preload the model
    const preloadModel = async () => {
      try {
        // First try to fetch just to see if the file is accessible
        const response = await fetch(modelPath);
        if (!response.ok) {
          throw new Error(`Model file not found: ${modelPath} (Status: ${response.status})`);
        }
        
        // If fetch succeeds, try to preload the actual model
        await preloadGLTF(modelPath);
        
        // If we get here, the model is ready to be used by the component
        console.log("Model preload complete. Ready for viewing.");
      } catch (error) {
        console.error("Error checking/preloading model:", error);
        setLoadingState("error");
        setErrorMessage(`Could not load model: ${error.message}`);
      }
    };
    
    preloadModel();
  }, [modelPath]);
  
  const handleAnimationsLoaded = (info) => {
    setModelInfo(info);
    setLoadingState("loaded");
  };
  
  const handleAnimationChange = (animationName) => {
    setCurrentAnimation(animationName);
  };
  
  const handleModelError = (error) => {
    setLoadingState("error");
    setErrorMessage(`Error loading model: ${error.message || 'Unknown error'}`);
  };
  
  const toggleAnimation = () => {
    if (modelRef.current?.toggleAnimation) {
      modelRef.current.toggleAnimation();
    }
  };
  
  const togglePlayPause = () => {
    if (modelRef.current?.togglePlayPause) {
      const newState = modelRef.current.togglePlayPause();
      setIsPlaying(newState);
    }
  };
  
  const resetCamera = () => {
    if (resetRef.current?.resetCamera) {
      resetRef.current.resetCamera();
    }
  };
  
  const toggleWireframe = () => {
    console.log('isDevMode', isDevMode)
    setWireframe(!wireframe);
  };
  
  const handleSpeedChange = (e) => {
    const newSpeed = parseFloat(e.target.value);
    setAnimationSpeed(newSpeed);
  };
  
  // Function to export/download the model
  const exportModel = () => {
    // Create a download link for the model
    const link = document.createElement('a');
    link.href = modelPath;
    link.download = modelPath.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // If there's an error loading the model
  if (loadingState === "error") {
    return (
      <div className={`model-viewer ${className || ""}`}>
        <div className="model-error">
          <div className="error-icon">❌</div>
          <div className="error-message">{errorMessage}</div>
          <div className="error-suggestion">
            Please check if the model file exists and is in a supported format (.glb/.gltf)
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`model-viewer ${className || ""}`}>
      <Canvas 
        key={sceneKey}
        pixelRatio={[1, 2]} 
        camera={{ position: cameraPosition, fov: cameraFov }}
        onCreated={({ gl }) => {
          gl.setClearColor(0x000000, 0);
        }}
      >
        <ambientLight intensity={0.5} />
        <directionalLight position={[10, 10, 5]} intensity={1} />
        <Suspense fallback={null}>
          <Model 
            modelPath={modelPath} 
            onAnimationsLoaded={handleAnimationsLoaded}
            onAnimationChange={handleAnimationChange}
            ref={modelRef}
            wireframe={wireframe}
            animationSpeed={animationSpeed}
            onError={handleModelError}
          />
          <CameraControls resetRef={resetRef} />
        </Suspense>
      </Canvas>
      
      {/* Loading indicator */}
      {loadingState === "loading" && (
        <div className="model-loader">
          <div className="loader-spinner"></div>
          <div>Loading 3D model...</div>
        </div>
      )}
      
      {/* Model info and animation controls */}
      {loadingState === "loaded" && (
        <>
          {modelInfo.hasAnimations && isDevMode && (
            <div className="animation-info">
              <span>Animation: {currentAnimation || 'None'}</span>
            </div>
          )}
          
          {isDevMode && (
            <div className="model-controls">
              <div className="control-group">
                <button onClick={toggleAnimation} disabled={!hasAnimations}>
                  Next Animation
                </button>
                <button onClick={togglePlayPause} disabled={!hasAnimations}>
                  {isPlaying ? 'Pause' : 'Play'}
                </button>
                <button onClick={resetCamera}>Reset Camera</button>
                <button onClick={toggleWireframe}>
                  {wireframe ? 'Solid' : 'Wireframe'} 
                </button>
                <div className="speed-control">
                  <label>Speed:</label>
                  <input
                    type="range"
                    min="0.1"
                    max="2"
                    step="0.1"
                    value={animationSpeed}
                    onChange={handleSpeedChange}
                  />
                  <span>{animationSpeed.toFixed(1)}x</span>
                </div>
              </div>
              <div className="camera-position">
                Camera: [{cameraPositionDisplay.map(n => n.toFixed(2)).join(', ')}]
              </div>
              <button className="animation-button download-button" onClick={exportModel}>
                Download Model
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
});

ModelViewer.propTypes = {
  modelPath: PropTypes.string.isRequired,
  className: PropTypes.string,
  cameraPosition: PropTypes.arrayOf(PropTypes.number),
  cameraFov: PropTypes.number
};

ModelViewer.defaultProps = {
  className: '',
  cameraPosition: [-10, 15, 15],
  cameraFov: 50
};

export default ModelViewer; 